<template>
  <div ref="rootRef" v-on-click-outside="onOutsideClick" class="select">
    <div
      v-if="label"
      ref="selectLabel"
      class="select__label"
      :style="{
        color: labelColor,
      }"
    >
      {{ label }}
      <Icon
        v-if="required"
        class="select__required"
        color="#D19B3F"
        mode="fill"
        value="required"
      />
      <!-- <span v-if="required" class="select__required">
        <Icon value='required' width='10' height='10' />
      </span> -->
    </div>
    <div
      ref="selectInput"
      :class="[
        'select__input',
        { 'select__input_no-border': !border },
        { select__input_disabled: disabled },
        { 'select__input_has-chips': hasChips },
        { select__input_error: error },
      ]"
      :style="fieldStyle"
      @click="handleInputClick"
    >
      <slot class="select-icon"></slot>
      <div
        :class="[
          'select__input-value',
          { 'select__input-value_selected': !isValueEmpty },
          { 'select__input-value_has-chips': hasChips },
        ]"
      >
        <Chips
          v-if="hasChips"
          :default-item="defaultChip"
          :has-border="false"
          :items="valueName"
          :item-clickable="chipClickable"
          :color-chip="colorChip"
          @itemClick="handleChipClick"
          @itemIconClick="handleChipIconClick"
        />
        <span v-else-if="isValueEmpty" class="select__placeholder">
          {{ placeholder }}
        </span>
        <span v-else>
          <slot name="selectedValue" :value="valueName">
            {{ valueName }}
          </slot>
        </span>
      </div>
      <div
        v-if="hasIcon"
        class="select__input-dropdown-button"
        :class="{ 'select__input-dropdown-button_close': iconOpened }"
      >
        <Icon
          v-if="!disabled"
          class="select__input-icon"
          color="#242424"
          mode="stroke"
          value="arrow_down"
        />
      </div>
    </div>
    <div v-if="openedVal" class="select__dropdown" :style="dropdownStyle">
      <div class="select__dropdown-topmenu">
        <SingleLineInput
          v-if="search || hasSearch"
          ref="selectSearch"
          class="select__dropdown-topmenu-search"
          placeholder="Поиск"
          title="Esc - очистить поиск, Enter - переключить все показанные опции"
          :value="searchTerm"
          @input="handleSearchInput"
          @onEnter="handleEnterClick"
          @onEsc="setSearchTerm('')"
        />
        <template v-if="hasMultipleSelectControls && multiple">
          <Icon
            class="select__dropdown-topmenu-icon"
            color="#242424"
            mode="fill"
            value="select-multiple"
            title="Отметить все"
            @click="selectAllValues"
          />
          <Icon
            class="select__dropdown-topmenu-icon"
            color="#242424"
            mode="fill"
            value="unselect-multiple"
            title="Сбросить все отметки"
            @click="unselectAllValues"
          />
        </template>
      </div>
      <div class="select__dropdown-main">
        <div ref="nodeScrollable" class="select__dropdown-items-wrapper">
          <List
            id="list"
            :items="paginated"
            :item-field-id="optionFieldId"
            :item-field-name="optionFieldName"
            :multiple="multiple"
            :toggle-type="multiple ? 'checkbox' : 'none'"
            :value="value"
            @input="handleListInput"
            @inputIdx="handleListInputIdx"
          >
            <template #itemContent="{ column, index }">
              <slot name="itemContent" :column="column" :index="index" />
            </template>
          </List>
        </div>
        <slot name="items-after" />
      </div>
      <li v-if="hasPaginate && hasPaginateAuto" class="pagination">
        <BaseButton
          class="pagination__button"
          :disabled="!hasPrevPage"
          name="«"
          @click="offset = 0"
        />
        <BaseButton
          class="pagination__button"
          :disabled="!hasPrevPage"
          name="‹"
          @click="offset -= limit"
        />
        <SingleLineInput
          :value="limit"
          placeholder="Количество строк"
          :formatted="true"
          @input="value => setLimitPaginate(value)"
          @onEnter="paginated"
        />
        <BaseButton
          class="pagination__button"
          :disabled="!hasNextPage"
          name="›"
          @click="offset += limit"
        />
        <BaseButton
          :disabled="!hasNextPage"
          name="»"
          @click="offset = optionsFiltered.length - limit"
        />
      </li>
    </div>
    <transition name="slide-y">
      <div v-if="error" id="text__error">{{ errorText }}</div>
    </transition>
  </div>
</template>

<script>
import { computed, ref } from 'vue';

import { filterItems } from '@/shared/helpers';
import { debounce } from '@/shared/helpers';
import { vOnClickOutside } from '@vueuse/components';
import { useScroll } from '@vueuse/core';

import BaseButton from './../Button';
import Chips from './../Chips';
import Icon from './../Icon';
import List from './../List';
import SingleLineInput from './../SingleLineInput';

export default {
  components: { Chips, Icon, List, SingleLineInput, BaseButton },
  directives: { onClickOutside: vOnClickOutside },
  props: {
    /* Has select border or not */
    border: { default: true, type: Boolean },
    /* Is chip clickable */
    chipClickable: { default: true, type: Boolean },
    /*  */
    debounce: { default: 500, type: Number },
    /* Name of default chip */
    defaultChip: { default: '', type: String },
    /* Disable option */
    disabled: { default: false, type: Boolean },
    /* Enables displaying 'not selected' option */
    displayNotSelected: { default: true, type: Boolean },
    /* Sets dropdown box height excluding search */
    dropdownHeight: { default: 200, type: Number },
    /* Border for field */
    fieldBorder: { default: '', type: String },
    /* Sets dropdown box height excluding search */
    hasIcon: { default: true, type: Boolean },
    /* Enables search on options */
    hasSearch: { default: false, type: Boolean },
    hasMultipleSelectControls: { default: false, type: Boolean },
    hasPaginate: { default: false, type: Boolean },
    limitPaginate: { default: 100, type: Number },
    emptyValuesInBack: { default: false, type: Boolean },
    /* Additional label on left top of select */
    label: { default: '', type: String },
    labelColor: { default: '', type: String },
    /* Enables multiple choice */
    multiple: { default: false, type: Boolean },
    /* Array of options inside dropdown */
    options: { required: true, type: Array },
    /* Field of object to value check */
    optionFieldId: { default: null, type: String },
    /* Specifies the displaying key of options' objects */
    optionFieldName: { default: 'name', type: String },
    /* Value of 'not selected' option */
    optionEmptyId: { default: '', type: String },
    /*  */
    openOnInputClick: { default: true, type: Boolean },
    /* Placeholder value */
    placeholder: { default: '', type: String },
    required: { default: false, type: Boolean },
    /* Enables search on options - to be deprecated. Use `hasSearch` */
    search: { default: false, type: Boolean },
    /* Output value. HINT: use v-model instead of (value + @input) to bind */
    value: { default: null, type: [Array, Number, String] },
    innerBorder: { default: '', type: String },
    colorChip: { default: 'var(--color-1)', type: String },
    termInitial: { default: '', type: String },
    dropdownWidth: { default: undefined, type: [Number, String] },
    openedValueForce: { default: false, type: Boolean },
    openedValue: { default: false, type: Boolean },
    iconOpenedValueForce: { default: false, type: Boolean },
    iconOpenedValue: { default: false, type: Boolean },
    isApply: { default: true, type: Boolean },
    error: { default: false, type: Boolean },
    errorText: { default: '', type: String },
  },
  emits: {
    chipClick: null,
    bottom: null,
    closed: null,
    input: null,
    inputIdx: null,
    inputClick: null,
    open: null,
    search: null,
    out: null,
  },
  setup() {
    const nodeScrollable = ref(null);
    const { arrivedState } = useScroll(nodeScrollable);
    const isBottom = computed(() => {
      return !!arrivedState.bottom;
    });

    return {
      isBottom,
      nodeScrollable,
    };
  },
  data: function () {
    return {
      dropdownWidthVal: 'inherit',
      isEnoughSpaceForDropdown: true,
      labelHeight: 0,
      opened: false,
      searchHeight: 0,
      searchTerm: '123',
      offset: 0,
      limit: this.limitPaginate,
      rootRef: null,
    };
  },
  computed: {
    openedVal() {
      return this.openedValueForce ? this.openedValue : this.opened;
    },
    iconOpened() {
      return this.iconOpenedValueForce ? this.iconOpenedValue : this.openedVal;
    },
    dropdownStyle() {
      const opened = this.openedVal;
      const labelHeight = this.label ? this.labelHeight + 'px - 0.5rem' : '0px';
      return {
        visibility: opened ? 'visible' : 'hidden',
        width: this.dropdownWidth ?? this.dropdownWidthVal,
        '--dd-height': opened ? this.fullDropdownHeight + 'px' : '0px',
        boxShadow: opened ? '0 16px 32px rgba(35, 39, 51, 0.1)' : 'none',
        '--items-height':
          this.dropdownHeight -
          16 -
          (this.search || this.hasSearch ? 8 : 0) +
          'px',
        top: !this.isEnoughSpaceForDropdown
          ? opened
            ? 'auto !important'
            : '0px'
          : '',
        bottom: !this.isEnoughSpaceForDropdown
          ? opened
            ? 'calc(100% - ' + labelHeight + ' - 1px)'
            : '0px'
          : '',
      };
    },
    emptyOptionId: () => '$_notSelected',
    fieldStyle() {
      const fieldStyle = {};
      const fieldBorder = this.fieldBorder;

      if (fieldBorder) {
        fieldStyle.border = fieldBorder;
      }
      if (this.innerBorder) fieldStyle.background = this.innerBorder;
      return fieldStyle;
    },
    fullDropdownHeight() {
      return (
        this.dropdownHeight +
        (this.search || this.hasSearch ? this.searchHeight : 0)
      );
    },
    hasChips() {
      //return this.multiple;
    },
    hasNextPage() {
      const nextOffset = this.offset + this.limit;
      return Boolean(
        this.optionsFiltered.slice(nextOffset, this.limit + nextOffset).length
      );
    },
    hasPaginateAuto() {
      return this.options.length > this.limitPaginate;
    },
    hasPrevPage() {
      const prevOffset = this.offset - this.limit;
      return Boolean(
        this.optionsFiltered.slice(prevOffset, this.limit + prevOffset).length
      );
    },
    isValueEmpty() {
      if (typeof this.value === 'number') return false;
      return (
        !this.value || (typeof this.value === 'string' && !this.value.length)
      );
    },
    notFoundOptionId: () => '$_notFound',
    optionsFiltered() {
      const optionFieldName = this.optionFieldName;
      let optionsFiltered = [];

      if (this.displayNotSelected) {
        optionsFiltered.push({
          [this.optionFieldId]: this.optionEmptyId,
          [optionFieldName]: 'Не выбрано',
        });
        //  Не выбрано // select__dropdown-item_empty
      }

      if (this.searchTerm) {
        const searchTerm = this.searchTerm.toLowerCase();

        this.options.forEach(item => {
          const itemVal = item[optionFieldName];
          if (itemVal == undefined) return;

          const itemValToCheck =
            typeof itemVal === 'string' ? itemVal : itemVal.toString();
          if (!itemValToCheck.toLowerCase().includes(searchTerm)) return;

          optionsFiltered.push(item);
        });

        if (!optionsFiltered.length) {
          optionsFiltered.push({
            [this.optionFieldId]: this.notFoundOptionId,
            [optionFieldName]: 'Не найдено',
          });
          // Не найдено // select__dropdown-item_no-results
        }

        const check = false;
        if (check) {
          console.log(
            filterItems(this.options, [
              {
                optionFieldName: searchTerm,
              },
            ])
          );
        }
      } else {
        this.options.forEach(item => optionsFiltered.push(item));
      }

      if (this.emptyValuesInBack) {
        optionsFiltered = optionsFiltered
          .filter(o => o?.[optionFieldName] || false)
          .concat(optionsFiltered.filter(o => !o?.[optionFieldName] || false));
      }

      return optionsFiltered;
    },
    paginated() {
      return this.limit > 0
        ? this.optionsFiltered.slice(this.offset, this.limit + this.offset)
        : this.optionsFiltered;
    },
    valueName() {
      const optionFieldId = this.optionFieldId;
      const optionFieldName = this.optionFieldName;
      const value = this.value;
      return this.multiple && value
        ? value.map(
            v =>
              this.options.find(o => o[optionFieldId] === v)?.[optionFieldName]
          )
        : this.options.find(o => o[optionFieldId] === value)?.[optionFieldName];
    },
  },
  watch: {
    isBottom(newValue) {
      if (newValue) {
        this.$emit('bottom');
      }
    },
  },
  created() {
    this.handleSearchInput = debounce(v => {
      this.setSearchTerm(v);
      this.$emit('search', v);
    }, this.debounce);

    this.setSearchTerm(this.termInitial);
  },
  mounted() {
    this.updateStyle();
    this.labelHeight = this.$refs.selectLabel?.clientHeight;
    this.searchHeight = this.$refs.selectSearch?.clientHeight || 44;
  },
  methods: {
    /* UI */
    handleChipClick(index) {
      this.$emit('chipClick', index);
    },
    handleChipIconClick(index) {
      console.log('handleChipIconClick', index);
      this.removeValueItem(index);
    },
    handleEnterClick() {
      console.log('handleEnterClick', this.optionsFiltered[0]);
    },
    handleListInput(value) {
      this.$emit('input', value);
      if (this.multiple) return;

      const event = new Event('selectCustom', { bubbles: true });
      const list = document.getElementById('list');
      list.dispatchEvent(event);

      this.opened = false;
    },
    handleListInputIdx(value) {
      const targetItem = this.paginated[value];
      const optionFieldId = this.optionFieldId;
      const optionIdx = this.options.findIndex(
        i => i[optionFieldId] === targetItem[optionFieldId]
      );
      this.$emit('inputIdx', optionIdx);
      if (this.multiple) return;

      this.opened = false;
    },
    handleInputClick(e) {
      this.updateStyle();

      if (this.openOnInputClick) {
        if (!this.disabled) {
          if (this.opened) {
            this.opened = false;
            this.$emit('closed');
          } else {
            this.opened = true;
            this.$emit('open');
          }
          this.setSearchTerm('');
          this.setLimitPaginate(this.limitPaginate);
        }
      } else {
        this.$emit('inputClick', e);
      }
    },
    /* Methods */
    indexOfVal(arg) {
      for (let i = 0; i < this.value.length; i++) {
        if (this.value[i] === arg) return i;
      }

      return -1;
    },
    selectAllValues() {
      const optionFieldId = this.optionFieldId;
      this.$emit(
        'input',
        this.options.map(op => op[optionFieldId])
      );
    },
    unselectAllValues() {
      this.$emit('input', []);
    },
    removeValueItem(index) {
      const newValue = this.value;
      newValue.splice(index, 1);
      this.$emit('input', newValue);
    },
    updateStyle() {
      const selectInputRef = this.$refs.selectInput;
      if (!selectInputRef) return;

      let dropdownWidthVal = selectInputRef.clientWidth;
      if (!dropdownWidthVal) {
        dropdownWidthVal = selectInputRef.getBoundingClientRect().width;
      }

      this.dropdownWidthVal = dropdownWidthVal + 1 + 'px';
      this.isEnoughSpaceForDropdown =
        window.innerHeight -
          (selectInputRef.getBoundingClientRect().bottom +
            this.fullDropdownHeight) >
        0;
    },
    /* Setters */
    setSearchTerm(value) {
      this.searchTerm = value;
    },
    setLimitPaginate(value) {
      this.limit = value;
    },
    onOutsideClick(e) {
      if (!this.openedValueForce && this.isApply && this.opened) {
        this.opened = false;
        this.$emit('out', e);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './Select';
</style>
